import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, adminApi, type AdminDropboxView, type DropboxEvent } from '../lib/api'
import { formatBytes, formatDate, toCurrentOrigin } from '../lib/format'
import { useDocumentTitle } from '../lib/useDocumentTitle'
import { AdminSections } from '../components/AdminSections'
import { Alert, Badge, CopyField, PageShell, ProgressBar, Spinner } from '../components/ui'
import { DangerZone, shortId } from '../components/DangerZone'

export function Admin() {
  const { token = '' } = useParams()
  const qc = useQueryClient()
  // True while a section form is open. Set by AdminSections; it lives here
  // because it is the query that has to stop polling.
  const [editingSections, setEditingSections] = useState(false)

  const query = useQuery({
    queryKey: ['admin-dropbox', token],
    queryFn: () => adminApi.view(token),
    // Polling exists so an upload made in another tab shows up without a
    // reload. While a section form is open it buys nothing and re-renders the
    // list the form is anchored in, so it is off for the duration.
    refetchInterval: editingSections ? false : 30_000,
  })

  const reopen = useMutation({
    mutationFn: () => adminApi.reopen(token),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-dropbox', token] }),
  })

  // The suffix is what tells one tab from another when the same dropbox is open
  // in both its admin and its recipient view. Called before the early returns —
  // hooks cannot live below them.
  useDocumentTitle(query.data ? `${query.data.dropbox.title} (admin)` : null)

  if (query.isPending) {
    return (
      <PageShell>
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Spinner /> Loading…
        </div>
      </PageShell>
    )
  }

  if (query.isError) {
    return (
      <PageShell>
        <Alert tone="error" title="Cannot open this dropbox">
          {query.error instanceof ApiError ? query.error.message : 'Unexpected error.'}
        </Alert>
      </PageShell>
    )
  }

  return (
    <View
      view={query.data}
      token={token}
      reopening={reopen.isPending}
      onReopen={() => reopen.mutate()}
      onEditingChange={setEditingSections}
      onChanged={() => qc.invalidateQueries({ queryKey: ['admin-dropbox', token] })}
    />
  )
}

function View({
  view,
  token,
  reopening,
  onReopen,
  onEditingChange,
  onChanged,
}: {
  view: AdminDropboxView
  token: string
  reopening: boolean
  onReopen: () => void
  onEditingChange: (editing: boolean) => void
  onChanged: () => void
}) {
  const { dropbox, sections, files, texts, quota, phase, readiness, links, events, maxFiles } = view
  const freeSlots = maxFiles > 0 ? Math.max(0, maxFiles - files.length) : Number.MAX_SAFE_INTEGER

  const filesBySection = useMemo(() => {
    const map = new Map<string, typeof files>()
    for (const f of files) map.set(f.sectionId, [...(map.get(f.sectionId) ?? []), f])
    return map
  }, [files])

  const textBySection = useMemo(() => new Map(texts.map((t) => [t.sectionId, t])), [texts])
  const satisfied = useMemo(
    () => new Map(readiness.sections.map((s) => [s.sectionId, s.satisfied])),
    [readiness],
  )
  const completeCount = files.filter((f) => f.status === 'complete').length

  return (
    <PageShell>
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={dropbox.status === 'ready' ? 'done' : 'neutral'}>
            {dropbox.status === 'ready' ? 'Submitted' : 'Awaiting submission'}
          </Badge>
          <Badge tone={phase === 'open' ? 'neutral' : phase === 'soft_closed' ? 'warn' : 'danger'}>
            {phase === 'open' ? 'Open' : phase === 'soft_closed' ? 'Uploads closed' : 'Expired'}
          </Badge>
          <span className="text-xs text-zinc-400">admin view</span>
        </div>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">{dropbox.title}</h1>
        {dropbox.description && (
          <p className="mt-2 text-zinc-600 dark:text-zinc-400">{dropbox.description}</p>
        )}
      </header>

      <div className="card mb-4 space-y-4 p-5 sm:p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Fact label="Owner">{dropbox.ownerEmail}</Fact>
          <Fact label="Id">
            <code className="font-mono text-xs">{shortId(dropbox.id)}</code>
          </Fact>
          <Fact label="Created">{formatDate(dropbox.createdAt)}</Fact>
          <Fact label="Uploads close">{formatDate(dropbox.softDeadline)}</Fact>
          <Fact label="Link expires">{formatDate(dropbox.hardDeadline)}</Fact>
          <Fact label="Marked ready">{dropbox.readyAt ? formatDate(dropbox.readyAt) : '—'}</Fact>
          <Fact label="Confirmation sent">
            {dropbox.confirmationSentAt ? formatDate(dropbox.confirmationSentAt) : 'not yet'}
          </Fact>
          {dropbox.recipientEmail && (
            <Fact label="Link mailed to">
              {dropbox.recipientEmail}
              <span className="text-zinc-400">
                {' · '}
                {dropbox.inviteSentAt ? formatDate(dropbox.inviteSentAt) : 'sending'}
              </span>
            </Fact>
          )}
        </div>

        <div>
          <div className="mb-1.5 flex items-baseline justify-between text-xs text-zinc-500">
            <span>
              {formatBytes(quota.usedBytes)} of {formatBytes(quota.maxBytes)} used
            </span>
            <span>
              {completeCount} file{completeCount === 1 ? '' : 's'}
            </span>
          </div>
          <ProgressBar value={quota.maxBytes ? quota.usedBytes / quota.maxBytes : 0} />
        </div>

        <div className="flex flex-wrap gap-2">
          <a href={adminApi.downloadAllUrl(token)} className="btn-primary">
            Download all as zip
          </a>
          {dropbox.status === 'ready' && (
            <button type="button" onClick={onReopen} disabled={reopening} className="btn-ghost">
              {reopening && <Spinner />}
              Reopen for editing
            </button>
          )}
        </div>
      </div>

      {links && (
        <div className="card mb-4 space-y-3 p-5 sm:p-6">
          <h2 className="text-sm font-semibold">Links</h2>
          <CopyField label="Recipient link — send this one" value={toCurrentOrigin(links.recipientUrl)} />
          <CopyField label="Admin link — keep this private, it never expires" value={toCurrentOrigin(links.adminUrl)} />
        </div>
      )}

      <AdminSections
        token={token}
        sections={sections}
        filesBySection={filesBySection}
        textBySection={textBySection}
        satisfied={satisfied}
        freeBytes={quota.freeBytes}
        freeSlots={freeSlots}
        onBusyChange={onEditingChange}
        onChanged={onChanged}
      />

      {events && events.length > 0 && <Timeline events={events} />}

      <DangerZone token={token} dropbox={dropbox} />
    </PageShell>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium text-zinc-500">{label}</div>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  )
}

const EVENT_LABEL: Record<string, string> = {
  'dropbox.created': 'Dropbox created',
  'dropbox.ready': 'Recipient marked it ready',
  'dropbox.unready': 'Marked not ready again',
  'dropbox.reopened': 'Reopened by admin',
  'dropbox.updated': 'Settings changed',
  'file.uploaded': 'File uploaded',
  'file.removed': 'File removed',
  'confirmation.sent': 'Confirmation email sent',
  'section.created': 'Section added',
  'section.updated': 'Section changed',
  'section.deleted': 'Section removed',
  // Plural on purpose: it is a fact about the list, not about any one section,
  // so there is no single id that would be honest.
  'sections.reordered': 'Sections reordered',
}

function Timeline({ events }: { events: DropboxEvent[] }) {
  return (
    <div className="card mt-4 p-5 sm:p-6">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">Activity</h2>
        <span className="text-xs text-zinc-400">
          {events.length} {events.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>
      {/* A busy dropbox logs an entry per uploaded file, so this list can run to
          hundreds and would otherwise push everything else off the page. Capped
          and scrolled in place; the newest are at the top, which is where the
          interest is. */}
      <ol className="max-h-80 space-y-3 overflow-y-auto pr-1">
        {events.map((e) => (
          <li key={e.id} className="flex gap-3 text-sm">
            <div className="mt-1.5 size-1.5 shrink-0 rounded-full bg-zinc-300 dark:bg-zinc-600" />
            <div className="min-w-0 flex-1">
              <div>
                {EVENT_LABEL[e.type] ?? e.type}
                {typeof e.payload?.filename === 'string' && (
                  <span className="text-zinc-500"> — {e.payload.filename}</span>
                )}
              </div>
              <div className="text-xs text-zinc-400">
                {formatDate(e.at)} · {e.actor}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}
